import * as React from 'react'
import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  showApproveDialog,
  showDeleteDialog,
  showHoldDialog,
  showRejectDialog,
  showSendToApproveDialog,
} from '../../store/slice/dialogSlice'
import { GridToolbar } from '../GridToolbar/GridToolbar'
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { Download, Print } from '@mui/icons-material'
import { Button } from '@mui/material'




export default function CommonGrid({
  data,
  isLoading,
  columns,
  columnVisibility,
  setColumnVisibility,
  handleUpdateData,
  dynamicKey,
  height,
  hiddenSearch,
  handleAddRow,
  showCheckbox,
  error,
  handleReset,
  hiddenContent,
  getRowClassName,
  hiddenRefresh,
  onCellEditCommit,
  setRowId,
  disableSelectionOnClick
}) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Methods
  const handleDoubleClick = (params) => {
    switch (pathname) {
      case '/users':
        navigate(`/users/${params.row[dynamicKey]}`)
        break
      case '/groups':
        navigate(`/groups/${params.row[dynamicKey]}`)
        break
      case '/requests':
        navigate(`/requests/${params.row[dynamicKey]}`)
        break
      case '/rfqs':
        navigate(`/rfqs/${params.row[dynamicKey]}`)
        break
      case '/approval-stages':
        navigate(`/approval-stages/${params.row[dynamicKey]}`)
        break
      case '/analysis-dimensions':
        navigate(`/analysis-dimensions/${params.row[dynamicKey]}`)
        break
      case '/analysis-codes':
        navigate(`/analysis-codes/${params.row[dynamicKey]}`)
        break
      case '/orders':
        navigate(`/orders/${params.row[dynamicKey]}`)
        break
      case '/bids':
        navigate(`/bids/${params.row[dynamicKey]}`)
        break
      case '/bid-comparison':
        navigate(`/bid-comparison/${params.row[dynamicKey]}`)
        break
      case '/vendors':
        navigate(`/vendors/${params.row[dynamicKey]}`)
        break
      case '/invoice-match':
        navigate(`/invoice-match/${params.row[dynamicKey]}`)
        break
      case '/payment':
        navigate(`/payment/${params.row[dynamicKey]}`)
        break
      default:
        break
    }
  }

  const handleNavigation = (id) => {
    switch (pathname) {
      case '/users':
        navigate(`/users/${id}`)
        break
      case '/groups':
        navigate(`/groups/${id}`)
        break
      case '/requests':
        navigate(`/requests/${id}`)
        break
      case '/approval-stages':
        navigate(`/approval-stages/${id}`)
        break
      case '/analysis-dimensions':
        navigate(`/analysis-dimensions/${id}`)
        break
      case '/analysis-codes':
        navigate(`/analysis-codes/${id}`)
        break
      case '/orders':
        navigate(`/orders/${id}`)
        break
      case '/bids':
        navigate(`/bids/${id}`)
        break
      case '/bid-comparison':
        navigate(`/bid-comparison/${id}`)
        break
      case '/vendors':
        navigate(`/supplier-evaluation`)
        break
      case '/payment':
        navigate(`/payment/${id}`)
        break
      default:
        break
    }
  }

  const handleRowDelete = () => {
    dispatch(showDeleteDialog())
  }

  const handleRowApprove = () => {
    dispatch(showApproveDialog())
  }

  const handleRowReject = () => {
    dispatch(showRejectDialog())
  }

  const handleRowHold = () => {
    dispatch(showHoldDialog())
  }

  const handleSendToApprove = () => {
    dispatch(showSendToApproveDialog())
  }

  const handleRowClick = (e) => {
    setRowId && setRowId(e?.id)
  }

  const gridSize = useSelector((state) => state.dataGridSlice.size)

  const [tableData, setTableData] = useState([])

  useEffect(() => {
    if (!isLoading) {
      setTableData(data?.data)
    }
  }, [isLoading, data, error])

  useEffect(() => {
    if (error) {
      setTableData([])
    }
  }, [isLoading, data, error])

  const [pageSize, setPageSize] = React.useState(100)


  const exportToExcel = (rows, columns) => {
    const filteredColumns = columns.filter(column => column.field !== 'actions');

    const ws = XLSX.utils.json_to_sheet(
      rows.map(row =>
        filteredColumns.reduce((acc, column) => {
          acc[column.headerName] = row[column.field];
          return acc;
        }, {})
      )
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, 'data.xlsx');
  };



  const printTable = (data, columns) => {
    const filteredColumns = columns.filter(column => column.field !== 'actions');

    const printContent = `
      <html>
        <head>
          <title>Print Table</title>
          <style>
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid black;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
            }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                ${filteredColumns.map(column => `<th>${column.headerName}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row => `
                <tr>
                  ${filteredColumns.map(column => `<td>${row[column.field]}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };


  return (
    <Box sx={{ height: height, width: '100%' }}>
      <DataGrid
        onCellEditCommit={onCellEditCommit}
        rows={tableData || []}
        columns={columns(
          handleNavigation,
          handleRowDelete,
          handleRowApprove,
          handleRowHold,
          handleRowReject,
          handleSendToApprove
        )}
        onRowDoubleClick={handleDoubleClick}
        onSelectionModelChange={(newSelection) => {
          handleUpdateData(tableData?.filter((row) => newSelection.includes(row[dynamicKey])))
        }}
        components={{
          Toolbar: () => (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ marginTop: 0.5, marginLeft: 0.5 }}>
                <Button startIcon={<Download />} onClick={() => exportToExcel(tableData, columns())} size="small">
                  Export
                </Button>
                <Button startIcon={<Print />} onClick={() => printTable(tableData, columns())} size="small" >
                  Print
                </Button>
              </Box>
              <GridToolbar
                hiddenContent={hiddenContent}
                handleAddRow={handleAddRow}
                hiddenSearch={hiddenSearch}
                hiddenRefresh={hiddenRefresh}
                hiddenExport={true}
                handleReset={handleReset}
              />
            </Box>
          ),
        }}
        density={gridSize}
        loading={isLoading}
        columnVisibilityModel={columnVisibility}
        onRowClick={(e) => handleRowClick(e)}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibility(newModel)}
        getRowId={(row) => row[dynamicKey]}
        checkboxSelection={showCheckbox}
        pageSize={pageSize}
        onPageSizeChange={(newPage) => setPageSize(newPage)}
        pagination

        showCellRightBorder
        disableSelectionOnClick={disableSelectionOnClick}
        getRowClassName={getRowClassName}
        rowHeight={40}
      />
    </Box>
  )
}
